import clsx from "clsx";
import { Minus, TrendingDown, TrendingUp } from "lucide-react";
import type { ReactNode } from "react";

interface CardProps {
    children: ReactNode;
    className?: string;
    hover?: boolean;
}

export function Card({ children, className, hover = false }: CardProps) {
    return (
        <div
            className={clsx(
                "app-content-card rounded-2xl border shadow-sm",
                hover && "transition-all duration-200 hover:shadow-md",
                className,
            )}
        >
            {children}
        </div>
    );
}

/**
 * Tone bingkai ikon judul. Default `neutral` (monokrom, adaptif light/dark) —
 * ungu hanya dipakai bila halaman benar-benar butuh penekanan, lewat
 * `iconTone="primary"`. Jangan kembalikan ungu sebagai default: 12+ kartu
 * memakai komponen ini dan semuanya akan terlihat generik ("AI slop").
 */
type CardHeaderIconTone = "neutral" | "primary";

const CARD_HEADER_ICON_TONES: Record<CardHeaderIconTone, string> = {
    neutral: "app-content-subtle app-text-primary",
    primary: "app-accent-soft app-accent-text",
};

interface CardHeaderProps {
    title: string;
    description?: string;
    action?: ReactNode;
    icon?: ReactNode;
    iconTone?: CardHeaderIconTone;
}

export function CardHeader({
    title,
    description,
    action,
    icon,
    iconTone = "neutral",
}: CardHeaderProps) {
    return (
        <div className="flex flex-col gap-3 border-b app-shell-border p-4 sm:flex-row sm:items-start sm:justify-between sm:p-5">
            <div className="flex min-w-0 items-start gap-3">
                {icon && (
                    <div
                        className={clsx(
                            "shrink-0 rounded-xl p-2",
                            CARD_HEADER_ICON_TONES[iconTone],
                        )}
                    >
                        {icon}
                    </div>
                )}
                <div className="min-w-0">
                    <h3 className="app-text-primary text-base font-semibold sm:text-lg">{title}</h3>
                    {description && (
                        <p className="app-shell-text-muted mt-0.5 text-sm">{description}</p>
                    )}
                </div>
            </div>
            {action && <div className="w-full shrink-0 sm:w-auto">{action}</div>}
        </div>
    );
}

interface CardContentProps {
    children: ReactNode;
    className?: string;
    noPadding?: boolean;
}

export function CardContent({ children, className, noPadding = false }: CardContentProps) {
    return <div className={noPadding ? className : clsx("p-5", className)}>{children}</div>;
}

export interface StatsTrend {
    /** Teks yang sudah diformat, mis. "+12.5%". */
    value: string;
    direction: "up" | "down" | "flat";
}

interface StatsCardProps {
    label: string;
    value: string | number;
    icon?: ReactNode;
    hint?: string;
    /** Selisih vs periode sebelumnya (opsional, dipakai dashboard). */
    trend?: StatsTrend;
}

const TREND_TONES: Record<StatsTrend["direction"], string> = {
    up: "bg-[var(--color-success-bg)] text-[var(--color-success-text)]",
    down: "bg-[var(--color-error-bg)] text-[var(--color-error-text)]",
    flat: "app-content-subtle app-shell-text-muted",
};

function TrendIcon({ direction }: { direction: StatsTrend["direction"] }) {
    if (direction === "up") return <TrendingUp className="h-3.5 w-3.5" />;
    if (direction === "down") return <TrendingDown className="h-3.5 w-3.5" />;
    return <Minus className="h-3.5 w-3.5" />;
}

export function StatsCard({ label, value, icon, hint, trend }: StatsCardProps) {
    return (
        <div className="app-content-card rounded-2xl border p-5 shadow-sm transition-all duration-200 hover:shadow-md">
            <div className="flex items-start gap-3">
                {/* Ikon polos tanpa bingkai — sama seperti CardHeader, pola
                    "ikon ungu di kotak ungu" dilarang supaya tidak generik. */}
                {icon && (
                    <span className="app-text-primary mt-0.5 shrink-0 [&>svg]:h-5 [&>svg]:w-5">
                        {icon}
                    </span>
                )}
                <div className="min-w-0 flex-1">
                    <p className="app-shell-text-muted truncate text-sm font-medium">{label}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                        <p className="app-text-primary text-2xl font-bold">{value}</p>
                        {trend && (
                            <span
                                className={clsx(
                                    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold",
                                    TREND_TONES[trend.direction],
                                )}
                            >
                                <TrendIcon direction={trend.direction} />
                                {trend.value}
                            </span>
                        )}
                    </div>
                    {hint && <p className="app-text-soft mt-1 text-xs">{hint}</p>}
                </div>
            </div>
        </div>
    );
}
